import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute } from '@angular/router';
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ApiService } from "../../shared/services/api.service";

@Component({
    selector: "app-verificar-certificado",
    templateUrl: "./verificar-certificado.component.html",
    styleUrls: ["./verificar-certificado.component.scss"],
})
export class VerificarCertificadoComponent implements OnInit, OnDestroy {
    public title = '';
    private titleTimeouts: ReturnType<typeof setTimeout>[] = [];
    public certificateSrc: SafeResourceUrl | null = null;
    private paramsSubscription!: Subscription;
    private langChangeSubscription!: Subscription;

    public filtersForm!: FormGroup;
    public showError = false;
    public showSpinner = false;
    public showNoDocText = false;
    public isMobileOrTabletDevice = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private formBuilder: FormBuilder,
        private apiService: ApiService
    ) {}

    ngOnInit(): void {
        this.isMobileOrTabletDevice = this.detectMobileOrTabletDevice();

        this.translate.get('verificar-certificado.titulo').subscribe((text: string) => {
            this.title = text;
            this.typeTitle(this.title, 'verify-certificate-title');
        });

        this.filtersForm = this.formBuilder.group({
            csv: ['', Validators.required] // TO-DO: añadir pattern formato CSV
        })

        this.paramsSubscription = this.activatedRoute.queryParams.subscribe(
            params => {
                // Si se ha introducido la ruta ya con el parámetro CSV
                if (params['csv']) {
                    // Actualizar campo CSV del form
                    this.filtersForm.get('csv')?.patchValue(params['csv']);

                    this.getCertificate();
                }
            }
        );

        // Para refrescar el título al cambiar de idioma
        this.langChangeSubscription = this.translate.onLangChange.subscribe((_event: LangChangeEvent) => {
            this.translate.get('verificar-certificado.titulo').subscribe((text: string) => {
                this.title = text;
                this.resetTitle('verify-certificate-title');
                this.typeTitle(this.title, 'verify-certificate-title');
            });
        });
    }

    ngOnDestroy(): void {
        this.paramsSubscription?.unsubscribe();
        this.langChangeSubscription?.unsubscribe();

        this.clearTitleTimeouts();
    }

    private typeTitle(text: string, id: string) {
        if (text.length > 0) {
            const element = document.getElementById(id);

            if (element) {
                element.innerHTML += text.charAt(0);
                this.titleTimeouts.push(setTimeout(() => this.typeTitle(text.slice(1), id), 20));
            }
            
        }
    }

    private clearTitleTimeouts() {
        for (let i = 0; i < this.titleTimeouts.length; i++) {
            clearTimeout(this.titleTimeouts[i]);
        }
        this.titleTimeouts = [];
    }

    private resetTitle(id: string) {
        this.clearTitleTimeouts();

        const titleElement = document.getElementById(id);
        if (titleElement) {
            titleElement.innerHTML = '';
        }
    }

    private detectMobileOrTabletDevice(): boolean {
        const userAgent = navigator.userAgent || (window as any).opera;
        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet|kindle|silk|playbook/i.test(userAgent.toLowerCase());
    }

    public resetForm() {
        this.filtersForm.reset();
    }

    public validateForm() {
        this.showError = false;

        if (this.filtersForm.invalid) {
            this.showError = true;

            setTimeout(() => {
                this.showError = false;
            }, 5000);
        } else {
            this.getCertificate();
        }
    }

    public closeCertificate() {
        this.certificateSrc = null;
    }

    private downloadPdf(blob: Blob | null, fileName: string) {
        if (blob) {
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
        }
    }

    /**
    * Convertir la respuesta a blob y mostrar/descargar el PDF
    */
    private transformBase64Resp(resp: any) {
        (async () => {
            const response = await fetch(
                `data:application/pdf;base64,${resp.base64Archivo}`
            );
            const blob = await response.blob();
            
            // En dispositivos móviles/tablets, descargar automáticamente en lugar de mostrar en iframe (falla la carga)
            if (this.isMobileOrTabletDevice) {
                this.downloadPdf(blob, resp.nombreArchivo);
            } else {
                const url = URL.createObjectURL(blob);
                this.certificateSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            }
        })();
    }

    private getCertificate() {
        this.certificateSrc = null;
        const filters = {
            idiomaID: this.apiService.getLanguageId(),
            csv: this.filtersForm.get('csv')?.value
        };

        this.showNoDocText = false;
        this.showSpinner = true;
        this.apiService.getCertificatePdf(filters).subscribe(
            resp => {
                this.transformBase64Resp(resp);
            },
            error => {
                this.showNoDocText = true;
            }
        ).add(() => {
            this.showSpinner = false;
        });
    }
}
